import { Collapse, Link, Stack, Typography, TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { CSSProperties, FC, PropsWithChildren, ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { DescriptionIcon } from '../../../../components/AgTable/cell-renderers/FieldNameRenderer';
import { IForbiddenErrorConfig } from '../../../../components/Fallback/GenericErrorFallback';
import { GenericFallbacksWrapper } from '../../../../components/Fallback/GenericFallbacksWrapper';
import { CollapseButton } from './CollapseButton';

type SubSectionProps = PropsWithChildren & {
  actionComponent?: React.JSX.Element;
  /** Right-aligned header controls, rendered before `link`. For controls that belong opposite
   *  the title rather than next to it — e.g. a paginator or an expand button. */
  actions?: ReactNode;
  cardPadding?: string;
  childrenContainerStyle?: CSSProperties;
  collapseProps?: {
    collapsible?: boolean;
    defaultExpanded?: boolean;
    /**
     * Keep the collapsed body out of the DOM (the house pattern — see AtlasConsole's
     * CollapsibleSection). Required for content that measures itself on mount: an echarts chart
     * mounted inside a collapsed body initialises against a zero-height box and stays blank after
     * expanding. Off by default, since unmounting discards whatever state the body holds.
     */
    unmountOnExit?: boolean;
  };
  endAdornment?: ReactNode;
  /** Forwarded to `GenericFallbacksWrapper`. Supplying it implies `withFallbacks`. */
  forbiddenFallback?: IForbiddenErrorConfig;
  link?: string | React.JSX.Element;
  noCard?: boolean;
  path?: string;
  style?: CSSProperties;
  /** Forwarded to `GenericFallbacksWrapper`. Supplying it implies `withFallbacks`. */
  suspenseFallback?: ReactNode;
  title: string;
  /** Wraps the section in `GenericFallbacksWrapper`, as `Panel` did for the Summary tab's cards.
   *  Opt-in, not always-on: that wrapper adds a Suspense boundary as well as an error boundary,
   *  and callers already wrapping `SubSection` themselves (e.g. ObjectivesSection,
   *  RecentCommentariesPanel) would see their error fallback move inside the section. */
  withFallbacks?: boolean;
};

const Wrapper = styled('div')`
  margin: 2rem 0;
  max-width: 100%;
`;
const HeaderWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
`;
const TitleAndAction = styled('div')`
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  /* Without this the title group's flex floor is its min-content width, so a wide actions group
     cannot take space from it and overflows the header instead. Pairs with the title's noWrap. */
  min-width: 0;
`;
// Self-contained so `HeaderWrapper` needs no change: `TitleAndAction` is width: 100%, which
// already pushes this group to the right edge. Centres its own contents vertically against the
// title rather than relying on the header's alignment.
const ActionsGroup = styled('div')`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  gap: 0.5rem;
  /* Space from a following link. Scoped to :not(:last-child) so a section with actions and no link
     keeps its controls flush to the right edge, and callers without actions are unaffected. */
  &:not(:last-child) {
    margin-right: 0.5rem;
  }
`;

interface CardProps {
  noShadow?: boolean;
  padding?: string;
}
const CardWrapper = styled('div')<CardProps>`
  ${({ noShadow }) =>
    !noShadow &&
    `border: 2px solid #ffffff;
  box-shadow: 0px -4px 40px rgba(255, 255, 255, 0.06), 0px 4px 24px rgba(16, 37, 62, 0.04),
    0px 24px 48px rgba(75, 82, 93, 0.02);
  border-radius: 4px;`};
  padding: ${({ padding }) => (padding ? padding : '12px 16px')};
  ${({ noShadow }) => noShadow && `padding: 1rem 0`};
  margin-top: ${({ noShadow }) => (noShadow ? '0' : '8px')};
  background: ${({ noShadow }) =>
    !noShadow &&
    `radial-gradient(
    100% 204.54% at 0% 0%,
    rgba(249, 250, 250, 0.8) 0%,
    rgba(255, 255, 255, 0.32) 100%
  )`};
`;

export const SubSectionTitle: FC<Omit<TypographyProps, 'component' | 'ref'>> = ({
  children,
  sx,
  ...props
}) => (
  <Typography
    component='h4'
    variant='body1'
    color='text.primary'
    sx={{ fontWeight: '500', ...sx }}
    {...props}
  >
    {children}
  </Typography>
);

export function SubSection(props: SubSectionProps) {
  const {
    children,
    endAdornment,
    title,
    actionComponent,
    actions,
    link,
    path,
    style,
    noCard,
    cardPadding,
    childrenContainerStyle,
    withFallbacks,
    forbiddenFallback,
    suspenseFallback,
  } = props;
  const collapseProps = {
    collapsible: false,
    defaultExpanded: true,
    unmountOnExit: false,
    ...props.collapseProps,
  };
  const navigate = useNavigate();
  const { collapsible, defaultExpanded, unmountOnExit } = collapseProps;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const onLinkClick = useCallback(() => {
    if (path) {
      navigate(path);
    }
  }, [navigate, path]);

  const section = (
    <Wrapper style={{ ...style }}>
      <HeaderWrapper data-testid='section-header'>
        <TitleAndAction>
          <Stack direction='row' gap='0.25rem' alignItems={'center'} minWidth={0}>
            {collapsible && <CollapseButton expanded={expanded} setExpanded={setExpanded} />}
            {/* `Panel`'s title was always `noWrap`. Match that only when `actions` is present, so
                sections without it keep the wrapping title their callers render today. */}
            <SectionTitle title={title} noWrap={!!actions} />
            {endAdornment && endAdornment}
          </Stack>
          {actionComponent && actionComponent}
        </TitleAndAction>
        {actions && <ActionsGroup>{actions}</ActionsGroup>}
        {link && (
          <Link
            color={'secondary'}
            component='button'
            underline='hover'
            onClick={onLinkClick}
            sx={{ '&.LinkButtonWithIcon:hover': { borderBottom: 'none' }, whiteSpace: 'nowrap' }}
          >
            {link}
          </Link>
        )}
      </HeaderWrapper>
      <Collapse in={expanded} unmountOnExit={unmountOnExit}>
        <CardWrapper noShadow={noCard} padding={cardPadding} style={childrenContainerStyle}>
          {children}
        </CardWrapper>
      </Collapse>
    </Wrapper>
  );

  // Supplying either fallback is treated as opting in, so passing one without `withFallbacks`
  // cannot silently leave a card with no boundary at all.
  if (!withFallbacks && !forbiddenFallback && !suspenseFallback) {
    return section;
  }

  // Wraps the whole section, title included, so an error replaces the entire card the way it did
  // under `Panel` — rather than leaving the header stranded above a fallback.
  return (
    <GenericFallbacksWrapper forbiddenFallback={forbiddenFallback} suspenseFallback={suspenseFallback}>
      {section}
    </GenericFallbacksWrapper>
  );
}

export function SectionTitle({
  title,
  description,
  noWrap,
}: {
  title: string;
  description?: string;
  /** Ellipsize instead of wrapping. Off by default so existing callers are unaffected. */
  noWrap?: boolean;
}) {
  return (
    <Stack direction='row' gap='0.25rem' alignItems={'center'} minWidth={0}>
      <Typography variant='body1' sx={{ fontWeight: '500' }} component={'h4'} noWrap={noWrap}>
        {title}
      </Typography>
      {description && <DescriptionIcon description={description} />}
    </Stack>
  );
}
